/**
 * Whether a SaleFish-synced project's inventory may be behind, and what to
 * say about it (PropPay's sync-failure banner, E7).
 *
 * A project linked to SaleFish Legacy shows units, prices and sales pulled by
 * Vercel Cron (server/services/salefish). When a pull fails, or stops happening,
 * every screen still renders — from the last pull — and nothing said so. A rep
 * quoting a price on a suite that sold an hour ago is the cost.
 *
 * TWO AUDIENCES, TWO SENTENCES. Everyone learns the one thing they can use:
 * the data may be behind, and as of when. Only platform admins learn WHOSE
 * side failed, because to a builder "the problem is on SaleFish's side — 503"
 * is noise they can do nothing about, and to whoever fixes it that is the
 * whole diagnosis. The server's own error text is the evidence:
 * `describeFailure` in server/services/salefish/client.ts prefixes every
 * vendor failure with "SaleFish …" or names SaleFish's rejection outright.
 */
import { dateFormat, getDisplayLocale, getDisplayTimeZone } from "@/lib/format";

import { interpolate, type MsgVars } from "@shared/i18n";

/**
 * A translator: the hook's `t` (useTranslation) satisfies it. Defaults to
 * English so the pure functions stay callable — and testable — without one.
 */
export type SyncTranslate = (key: string, en: string, vars?: MsgVars) => string;
const english: SyncTranslate = (_key, en, vars) => interpolate(en, vars);

/** The fields of GET /api/projects/:id/salefish/config the banner reads. */
export interface SalefishSyncState {
  configured?: boolean;
  isActive?: boolean | null;
  /** Which Legacy API the link is on: a high-rise link owns only the suites it brought (shared/salefishOwnership.ts). */
  apiType?: "high-rise" | "low-rise" | null;
  /** pull_only, push_only or bidirectional: whether a hold here reaches Legacy (shared/salefishOwnership.ts holdsReachLegacy). */
  syncMode?: string | null;
  lastSyncAt?: string | null;
  lastSyncStatus?: "success" | "error" | "syncing" | null;
  lastSyncError?: string | null;
  initialSyncCompleted?: boolean | null;
  credentialsConfigured?: boolean;
}

/** Availability runs every minute and the catalog every five; half an hour of silence is a stalled schedule. */
export const STALE_AFTER_MS = 30 * 60 * 1000;

export type FailedSide = "salefish" | "console" | "schedule" | "setup";

export interface SyncBanner {
  /** The sentence everyone sees. */
  text: string;
  /** Platform admins only: whose side, and why. */
  adminDetail: string | null;
  side: FailedSide | null;
}

// The reader's locale for what is SHOWN; `day()` below only compares two
// strings from the same formatter, so the locale cannot change its answer.
function fmt(options: Intl.DateTimeFormatOptions) {
  return dateFormat({ timeZone: getDisplayTimeZone(), ...options }, getDisplayLocale());
}

/**
 * "Showing data as of 2:15 p.m." today, with the date on any other day.
 * On the project's clock, stated rather than taken from the browser: a rep
 * travelling should read the office's time, and the office is wherever the
 * project is (Central Time on a Texas project).
 */
export function staleNote(
  lastSyncAt: string | null | undefined,
  now: Date = new Date(),
  t: SyncTranslate = english,
): string {
  const at = lastSyncAt ? new Date(lastSyncAt) : null;
  if (!at || Number.isNaN(at.getTime())) return t("shellStatusLastSyncedData", "Showing the last synced data.");
  const day = (d: Date) => fmt({ dateStyle: "short" }).format(d);
  // "2:15 p.m." already ends the sentence; the message supplies the full stop,
  // so it is taken off the time rather than doubled.
  const time = fmt({ hour: "numeric", minute: "2-digit" }).format(at).replace(/\.$/, "");
  if (day(at) === day(now)) return t("shellStatusDataAsOfTime", "Showing data as of {time}.", { time });
  const date = fmt({ month: "short", day: "numeric" }).format(at);
  return t("shellStatusDataAsOfDate", "Showing data as of {date}, {time}.", { date, time });
}

/** Whose failure an error string describes. */
export function failedSide(error: string | null | undefined): "salefish" | "console" {
  const text = error ?? "";
  // Vendor failures are phrased "SaleFish GET /x returned …", "SaleFish
  // rejected the service-account credentials (401)", "Not found (404) at /x".
  if (/\b(?:SaleFish|Legacy)\b|\(40[0-9]\)|\(5\d\d\)|\bnot found \(404\)/i.test(text)) return "salefish";
  return "console";
}

/** The banner for a project, or null when there is nothing to say. */
export function syncBanner(
  state: SalefishSyncState | null | undefined,
  now: Date = new Date(),
  t: SyncTranslate = english,
): SyncBanner | null {
  if (!state?.configured || state.isActive === false) return null;
  const note = staleNote(state.lastSyncAt, now, t);
  const sideWords = (side: "salefish" | "console") =>
    side === "salefish" ? t("shellStatusSideLegacy", "Legacy’s side") : t("shellStatusSideOurs", "our side");

  if (state.credentialsConfigured === false) {
    return {
      text: t("shellStatusCouldNotGetLatest", "Couldn’t get the latest data from Legacy. {note}", { note }),
      adminDetail: t(
        "shellStatusAdminNoServiceAccount",
        "Legacy sync is off: the service account (SALEFISH_API_USERNAME / SALEFISH_API_PASSWORD) is not configured on this deployment.",
      ),
      side: "setup",
    };
  }

  if (!state.lastSyncAt && !state.initialSyncCompleted) {
    if (state.lastSyncStatus === "error") {
      const side = failedSide(state.lastSyncError);
      return {
        text: t("shellStatusNotLoadedYet", "Inventory from Legacy hasn’t loaded yet."),
        adminDetail: state.lastSyncError
          ? t("shellStatusAdminFirstFailedError", "The first sync failed on {side} — {error}.", { side: sideWords(side), error: state.lastSyncError })
          : t("shellStatusAdminFirstFailed", "The first sync failed on {side}.", { side: sideWords(side) }),
        side,
      };
    }
    return {
      text: t("shellStatusStillLoading", "Inventory from Legacy is still loading — the first sync hasn’t finished."),
      adminDetail: null,
      side: null,
    };
  }

  if (state.lastSyncStatus === "error") {
    const side = failedSide(state.lastSyncError);
    return {
      text: t("shellStatusCouldNotGetLatest", "Couldn’t get the latest data from Legacy. {note}", { note }),
      adminDetail: state.lastSyncError
        ? t("shellStatusAdminLastFailedError", "The last sync failed on {side} — {error}.", { side: sideWords(side), error: state.lastSyncError })
        : t("shellStatusAdminLastFailed", "The last sync failed on {side}.", { side: sideWords(side) }),
      side,
    };
  }

  const at = state.lastSyncAt ? new Date(state.lastSyncAt).getTime() : NaN;
  if (Number.isFinite(at) && now.getTime() - at > STALE_AFTER_MS) {
    return {
      text: t("shellStatusMayBeBehind", "Inventory may be behind. {note}", { note }),
      adminDetail: t(
        "shellStatusAdminScheduleStalled",
        "No sync has completed for over half an hour — the scheduled sync may not be running (check the /api/crons/sync/* runs in Vercel).",
      ),
      side: "schedule",
    };
  }

  return null;
}
